import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { verifyAdminAccess, subscribeToWadeActions } from '../../services/adminService';
import {
    Sun,
    ChevronUp,
    ChevronDown,
    MoreHorizontal,
    Check,
    ArrowRight,
    Sparkles,
    X,
    Trash2,
    Edit2,
    Plus,
    FolderPlus,
    Archive,
    Calendar,
    RotateCcw,
    BarChart3,
    Flag,
    Moon,
    Flame,
    LayoutGrid,
    Layout,
    Activity,
    LogOut
} from 'lucide-react';
import { useAppState, Task, Project, CalendarEvent } from '../../contexts/AppContext';
import AddTaskModal from '../../components/app/AddTaskModal';
import ProjectModal from '../../components/app/ProjectModal';
import { getCurrentLocationWeather, getWeatherEmoji, WeatherData } from '../../services/weatherService';

// Helper function for priority colors
const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
        case 'high':
            return '#D66B5C';
        case 'medium':
            return '#E8A547';
        default:
            return '#7B8C9B';
    }
};

export default function AppToday() {
    const navigate = useNavigate();
    const {
        tasks,
        projects,
        archivedProjects,
        events,
        toggleTaskComplete,
        updateTask,
        softDeleteTask,
        softDeleteProject,
        archiveProject,
        unarchiveProject,
        getTasksByProject,
        addSubtask,
        toggleSubtaskComplete,
        deleteSubtask,
        trashedTasks,
        trashedEvents,
        trashedProjects,
        restoreTask,
        restoreEvent,
        restoreProject,
        permanentlyDeleteTask,
        permanentlyDeleteEvent,
        permanentlyDeleteProject,
    } = useAppState();
    const [briefingExpanded, setBriefingExpanded] = useState(true);
    const [suggestionDismissed, setSuggestionDismissed] = useState(false);
    const [activeFilter, setActiveFilter] = useState<'all' | 'priority' | 'project'>('all');
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'list' | 'time'>('list');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [editTask, setEditTask] = useState<Task | null>(null);
    const [editProject, setEditProject] = useState<Project | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [openProjectMenuId, setOpenProjectMenuId] = useState<string | null>(null);
    const [showCompleted, setShowCompleted] = useState(false);
    const [showTrashBin, setShowTrashBin] = useState(false);
    const [showArchivedProjects, setShowArchivedProjects] = useState(false);
    const [weather, setWeather] = useState<WeatherData | null>(null);
    const { user } = useAuth();
    const isAdmin = useMemo(() => (user && 'uid' in user) ? verifyAdminAccess((user as any).uid) : false, [user]);
    const [wadeActions, setWadeActions] = useState<any[]>([]);

    console.log("Wade Actions Log:", wadeActions);

    // Subscribe to Wade's actions if admin
    useEffect(() => {
        if (isAdmin) {
            return subscribeToWadeActions(setWadeActions);
        }
    }, [isAdmin]);

    // Fetch weather on mount
    useEffect(() => {
        getCurrentLocationWeather().then(setWeather);
    }, []);

    // Filter tasks
    const getFilteredTasks = () => {
        let filtered = tasks.filter((t) => !t.completed); // Exclude completed

        if (selectedProjectId) {
            filtered = filtered.filter((t) => t.projectId === selectedProjectId);
        }

        if (activeFilter === 'priority') {
            filtered = filtered.filter((t) => t.priority === 'high' || t.priority === 'medium');
            filtered = [...filtered].sort((a, b) => {
                const order = { high: 0, medium: 1, normal: 2 };
                return order[a.priority] - order[b.priority];
            });
        }

        if (viewMode === 'time') {
            filtered = [...filtered].sort((a, b) => {
                if (!a.timeStart) return 1;
                if (!b.timeStart) return -1;
                return a.timeStart.localeCompare(b.timeStart);
            });
        }

        return filtered;
    };

    // Get completed tasks
    const getCompletedTasks = () => {
        let filtered = tasks.filter((t) => t.completed);

        if (selectedProjectId) {
            filtered = filtered.filter((t) => t.projectId === selectedProjectId);
        }

        return filtered;
    };

    const displayTasks = getFilteredTasks();
    const completedTasks = getCompletedTasks();
    const taskCount = tasks.filter((t) => !t.completed).length;

    // Filter events for today only
    const todayStr = new Date().toISOString().split('T')[0];
    const todayEvents = events.filter(e => !e.deletedAt && e.date === todayStr);

    const handleDeleteTask = (taskId: string) => {
        softDeleteTask(taskId);
        setOpenMenuId(null);
    };

    const handleEditTask = (task: Task) => {
        setEditTask(task);
        setShowAddModal(true);
        setOpenMenuId(null);
    };

    const handleDeleteProject = (projectId: string) => {
        softDeleteProject(projectId, 'unassign');
        setOpenProjectMenuId(null);
        if (selectedProjectId === projectId) {
            setSelectedProjectId(null);
        }
    };

    const handleEditProject = (project: Project) => {
        setEditProject(project);
        setShowProjectModal(true);
        setOpenProjectMenuId(null);
    };

    const handleCloseProjectModal = () => {
        setShowProjectModal(false);
        setEditProject(null);
    };

    // Helper function to parse time string like "10:00 AM" to minutes since midnight
    const parseTimeToMinutes = (timeStr: string | undefined): number | null => {
        if (!timeStr) return null;
        const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
        if (!match) return null;
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3]?.toUpperCase();
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        return hours * 60 + minutes;
    };

    // Calculate total scheduled time for today's tasks (in minutes)
    const calculateScheduledTime = () => {
        let totalMinutes = 0;
        const today = new Date().toISOString().split('T')[0];

        tasks.filter(t => !t.completed && !t.deletedAt).forEach(task => {
            // Only count tasks for today or tasks without a due date (assumed today)
            const isToday = !task.dueDate || task.dueDate === today;
            if (isToday && task.timeStart && task.timeEnd) {
                const start = parseTimeToMinutes(task.timeStart);
                const end = parseTimeToMinutes(task.timeEnd);
                if (start !== null && end !== null && end > start) {
                    totalMinutes += end - start;
                }
            } else if (isToday) {
                // Tasks without specific times - assume 30 min default
                totalMinutes += 30;
            }
        });

        return totalMinutes;
    };

    // Find the best task to suggest moving (lowest priority, latest time)
    const findMovableTask = (): Task | null => {
        const today = new Date().toISOString().split('T')[0];
        const todayTasks = tasks.filter(t =>
            !t.completed &&
            !t.deletedAt &&
            (!t.dueDate || t.dueDate === today) &&
            t.priority !== 'high' // Don't suggest moving high priority tasks
        );

        if (todayTasks.length === 0) return null;

        // Sort by priority (normal first, then medium) and then by time (latest first)
        const sorted = [...todayTasks].sort((a, b) => {
            const priorityOrder = { normal: 0, medium: 1, high: 2 };
            if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
                return priorityOrder[a.priority] - priorityOrder[b.priority];
            }
            // If same priority, prefer later tasks
            const aTime = parseTimeToMinutes(a.timeStart) || 0;
            const bTime = parseTimeToMinutes(b.timeStart) || 0;
            return bTime - aTime;
        });

        return sorted[0] || null;
    };

    const scheduledMinutes = calculateScheduledTime();
    const scheduledHours = Math.round(scheduledMinutes / 60 * 10) / 10;
    const isOverloaded = scheduledMinutes > 480; // More than 8 hours
    const movableTask = isOverloaded ? findMovableTask() : null;

    const handleMoveTask = () => {
        if (movableTask) {
            // Move the task to tomorrow
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowStr = tomorrow.toISOString().split('T')[0];
            updateTask(movableTask.id, { dueDate: tomorrowStr });
        }
        setSuggestionDismissed(true);
    };

    const handleCloseTaskModal = () => {
        setShowAddModal(false);
        setEditTask(null);
    };

    return (
        <div className="pt-4 leading-[1.5] dark:bg-dark-bg min-h-screen">
            {/* Add Task Modal */}
            <AddTaskModal
                isOpen={showAddModal}
                onClose={handleCloseTaskModal}
                editTask={editTask}
            />

            {/* Project Modal */}
            <ProjectModal
                isOpen={showProjectModal}
                onClose={handleCloseProjectModal}
                editProject={editProject}
            />

            {/* Trash Bin Modal */}
            {showTrashBin && (
                <TrashBinModal
                    trashedTasks={trashedTasks}
                    trashedEvents={trashedEvents}
                    trashedProjects={trashedProjects}
                    onRestoreTask={restoreTask}
                    onRestoreEvent={restoreEvent}
                    onRestoreProject={restoreProject}
                    onDeleteTask={permanentlyDeleteTask}
                    onDeleteEvent={permanentlyDeleteEvent}
                    onDeleteProject={permanentlyDeleteProject}
                    onClose={() => setShowTrashBin(false)}
                />
            )
            }

            {/* Daily Briefing Card */}
            <section className="mb-4 px-4">
                <div className="bg-white dark:bg-dark-card rounded-2xl shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                    <div className="p-4">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-base font-semibold text-[#1A2832] dark:text-white">
                                Daily Briefing
                            </span>
                            <button
                                onClick={() => setBriefingExpanded(!briefingExpanded)}
                                className="w-6 h-6 flex items-center justify-center"
                            >
                                {briefingExpanded ? (
                                    <ChevronUp className="w-[18px] h-[18px] text-[#7B8C9B]" />
                                ) : (
                                    <ChevronDown className="w-[18px] h-[18px] text-[#7B8C9B]" />
                                )}
                            </button>
                        </div>

                        {briefingExpanded && (
                            <>
                                {/* Weather Widget */}
                                <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-dark-elevated dark:to-dark-card border border-amber-100 dark:border-dark-border">
                                    <div className="w-10 h-10 flex items-center justify-center text-2xl">
                                        {weather ? getWeatherEmoji(weather.icon) : '☀️'}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-lg font-semibold text-[#1A2832] dark:text-white">
                                                {weather ? `${weather.temp}°F` : '...'}
                                            </span>
                                            <span className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary capitalize">
                                                {weather?.description || 'Loading...'}
                                            </span>
                                        </div>
                                        {weather?.city && (
                                            <span className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">{weather.city}</span>
                                        )}
                                    </div>
                                    {weather && (
                                        <div className="text-right text-xs text-[#7B8C9B] dark:text-dark-text-muted">
                                            <div>H: {weather.tempMax}°</div>
                                            <div>L: {weather.tempMin}°</div>
                                        </div>
                                    )}
                                </div>

                                {/* Calendar Mini Widget */}
                                <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-dark-elevated dark:to-dark-card border border-blue-100 dark:border-dark-border">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-blue-600 dark:text-dark-teal-light" />
                                            <span className="text-sm font-semibold text-[#1A2832] dark:text-white">Today's Schedule</span>
                                        </div>
                                        {/* Sync Status */}
                                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/80 dark:bg-dark-elevated">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                            <span className="text-xs text-[#4A5C6A] dark:text-dark-text-secondary">Synced</span>
                                        </div>
                                    </div>
                                    {/* Events list */}
                                    <div className="space-y-2">
                                        {todayEvents.slice(0, 3).map((event) => (
                                            <div key={event.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/60 dark:bg-dark-elevated/60">
                                                <div
                                                    className="w-1 h-8 rounded-full"
                                                    style={{ backgroundColor: event.color || '#4285F4' }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-[#1A2832] dark:text-dark-text truncate">{event.title}</p>
                                                    <p className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">{event.timeStart} - {event.timeEnd}</p>
                                                </div>
                                                {event.location && (
                                                    <span className="text-xs text-[#7B8C9B] truncate max-w-[80px]">{event.location}</span>
                                                )}
                                            </div>
                                        ))}
                                        {todayEvents.length === 0 && (
                                            <p className="text-sm text-[#7B8C9B] text-center py-2">No events scheduled</p>
                                        )}
                                    </div>
                                    {todayEvents.length > 3 && (
                                        <button
                                            onClick={() => navigate('/app/calendar')}
                                            className="w-full mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            +{todayEvents.length - 3} more events
                                        </button>
                                    )}
                                </div>

                                <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary mb-3">
                                    You have <span className="font-semibold text-[#1A2832] dark:text-white">{taskCount} tasks</span> and <span className="font-semibold text-[#1A2832] dark:text-white">{todayEvents.length} meetings</span> today.
                                </p>

                                {/* Energy Indicator & Productivity Peaks */}
                                <div className="p-3 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {/* Energy Bar */}
                                            <div className="relative w-10 h-10">
                                                <svg className="w-10 h-10 -rotate-90">
                                                    <circle
                                                        cx="20"
                                                        cy="20"
                                                        r="16"
                                                        stroke="#E5E7EB"
                                                        strokeWidth="4"
                                                        fill="none"
                                                    />
                                                    <circle
                                                        cx="20"
                                                        cy="20"
                                                        r="16"
                                                        stroke={(() => {
                                                            const hour = new Date().getHours();
                                                            if (hour >= 9 && hour <= 11) return '#10B981'; // Peak - green
                                                            if (hour >= 7 && hour <= 17) return '#F59E0B'; // Active - amber
                                                            return '#6B7280'; // Low - gray
                                                        })()}
                                                        strokeWidth="4"
                                                        fill="none"
                                                        strokeDasharray="100"
                                                        strokeDashoffset={(() => {
                                                            const hour = new Date().getHours();
                                                            if (hour >= 9 && hour <= 11) return 20; // 80% energy
                                                            if (hour >= 7 && hour <= 17) return 40; // 60% energy
                                                            return 70; // 30% energy
                                                        })()}
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[#1A2832]">
                                                    {(() => {
                                                        const hour = new Date().getHours();
                                                        if (hour >= 9 && hour <= 11) return '80%';
                                                        if (hour >= 7 && hour <= 17) return '60%';
                                                        return '30%';
                                                    })()}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-[#1A2832]">
                                                    {(() => {
                                                        const hour = new Date().getHours();
                                                        if (hour >= 9 && hour <= 11) return '🔥 Peak Energy';
                                                        if (hour >= 7 && hour <= 17) return '⚡ Active Hours';
                                                        return '🌙 Wind Down';
                                                    })()}
                                                </p>
                                                <p className="text-xs text-[#4A5C6A]">Peak hours: 9-11 AM</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Flame className="w-4 h-4 text-orange-500" />
                                            <span className="text-xs font-medium text-[#4A5C6A]">
                                                {(() => {
                                                    const hour = new Date().getHours();
                                                    if (hour >= 9 && hour <= 11) return 'Best for deep work';
                                                    if (hour >= 14 && hour <= 16) return 'Good for meetings';
                                                    return 'Light tasks recommended';
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* Next Up Section */}
            <section className="mb-4 px-4">
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-xl font-semibold text-[#1A2832] dark:text-white">Next Up</span>
                    <ArrowRight className="w-[18px] h-[18px] text-[#7B8C9B]" />
                </div>
                <div className="space-y-3">
                    {tasks.filter((t) => !t.completed).slice(0, 2).map((task) => (
                        <div
                            key={task.id}
                            className="bg-white dark:bg-dark-card rounded-xl p-3 shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-dark"
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <h4 className="text-base font-semibold text-[#1A2832] dark:text-white">{task.title}</h4>
                                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary">
                                        {task.timeStart ? `${task.timeStart} - ${task.timeEnd}` : 'No time set'}
                                    </p>
                                </div>
                                <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: getPriorityColor(task.priority) }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Quick Actions Row */}
            <section className="mb-4 px-4">
                <div className="flex gap-3 overflow-x-auto [&::-webkit-scrollbar]:hidden">
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center justify-center gap-2 rounded-xl bg-white dark:bg-dark-card text-[#0D5F6B] dark:text-dark-teal-light border border-[#DCE4EA] dark:border-dark-border px-6 py-3 whitespace-nowrap shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-dark"
                    >
                        <Plus className="w-4 h-4" />
                        <span className="text-base font-semibold">Add Task</span>
                    </button>
                    <button
                        onClick={() => setShowProjectModal(true)}
                        className="flex items-center justify-center gap-2 rounded-xl bg-white dark:bg-dark-card text-[#0D5F6B] dark:text-dark-teal-light border border-[#DCE4EA] dark:border-dark-border px-6 py-3 whitespace-nowrap shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-dark"
                    >
                        <FolderPlus className="w-4 h-4" />
                        <span className="text-base font-semibold">Add Project</span>
                    </button>
                    <button
                        onClick={() => navigate('/app/focus')}
                        className="flex items-center justify-center rounded-xl bg-[#0D5F6B] text-white px-6 py-3 whitespace-nowrap shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]"
                    >
                        <span className="text-base font-semibold">Focus Mode</span>
                    </button>
                    <button
                        onClick={() => navigate('/app/calendar')}
                        className="flex items-center justify-center rounded-xl bg-white dark:bg-dark-card text-[#0D5F6B] dark:text-dark-teal-light border border-[#DCE4EA] dark:border-dark-border px-6 py-3 whitespace-nowrap shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-dark"
                    >
                        <span className="text-base font-semibold">View Week</span>
                    </button>
                </div>
            </section >

            {/* Tools Section */}
            < section className="mb-4 px-4" >
                <h3 className="text-sm font-medium text-[#7B8C9B] dark:text-dark-text-secondary mb-2">Tools</h3>
                <div className="grid grid-cols-4 gap-2">
                    <button
                        onClick={() => navigate('/app/schedule')}
                        className="flex flex-col items-center gap-1 p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-dark hover:shadow-md transition-shadow"
                    >
                        <Sparkles className="w-5 h-5 text-[#0D5F6B]" />
                        <span className="text-xs text-[#4A5C6A] dark:text-dark-text-secondary">Optimizer</span>
                    </button>
                    <button
                        onClick={() => navigate('/app/command-center')}
                        className="flex flex-col items-center gap-1 p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-dark hover:shadow-md transition-shadow"
                    >
                        <LayoutGrid className="w-5 h-5 text-[#F59E0B]" />
                        <span className="text-xs text-[#4A5C6A] dark:text-dark-text-secondary">Command</span>
                    </button>
                    <button
                        onClick={() => navigate('/app/briefing')}
                        className="flex flex-col items-center gap-1 p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-dark hover:shadow-md transition-shadow"
                    >
                        <Sun className="w-5 h-5 text-[#E8A547]" />
                        <span className="text-xs text-[#4A5C6A] dark:text-dark-text-secondary">Briefing</span>
                    </button>
                    <button
                        onClick={() => navigate('/app/debrief')}
                        className="flex flex-col items-center gap-1 p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-dark hover:shadow-md transition-shadow"
                    >
                        <Moon className="w-5 h-5 text-[#1A2832]" />
                        <span className="text-xs text-[#4A5C6A] dark:text-dark-text-secondary">Debrief</span>
                    </button>
                    <button
                        onClick={() => navigate('/app/habits')}
                        className="flex flex-col items-center gap-1 p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-dark hover:shadow-md transition-shadow"
                    >
                        <Flame className="w-5 h-5 text-[#EF4444]" />
                        <span className="text-xs text-[#4A5C6A] dark:text-dark-text-secondary">Habits</span>
                    </button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                    <button
                        onClick={() => navigate('/app/goals')}
                        className="flex items-center justify-center gap-2 p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-dark hover:shadow-md transition-shadow"
                    >
                        <span className="text-lg">🎯</span>
                        <span className="text-sm font-medium text-[#4A5C6A] dark:text-dark-text-secondary">Goals</span>
                    </button>
                    <button
                        onClick={() => navigate('/app/analytics')}
                        className="flex items-center justify-center gap-2 p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-dark hover:shadow-md transition-shadow"
                    >
                        <BarChart3 className="w-5 h-5 text-[#0D5F6B]" />
                        <span className="text-sm font-medium text-[#4A5C6A] dark:text-dark-text-secondary">Analytics</span>
                    </button>
                    <button
                        onClick={() => navigate('/app/focus')}
                        className="flex items-center justify-center gap-2 p-3 bg-[#0D5F6B] rounded-xl shadow-sm hover:shadow-md transition-shadow"
                    >
                        <span className="text-sm font-medium text-white">Focus Mode</span>
                    </button>
                </div>
            </section >

            {/* AI Suggestions Card - Shows when schedule is overloaded */}
            {
                !suggestionDismissed && isOverloaded && movableTask && (
                    <section className="mb-4 px-4">
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-[18px] h-[18px] text-amber-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-amber-800 mb-1">
                                        ⚡ You have {scheduledHours} hours scheduled today
                                    </p>
                                    <p className="text-base text-[#1A2832] mb-4">
                                        That's a lot! Should we move "{movableTask.title}" to tomorrow?
                                    </p>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleMoveTask}
                                            className="flex items-center justify-center rounded-xl bg-[#0D5F6B] text-white px-4 py-2 shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]"
                                        >
                                            <span className="text-base font-semibold whitespace-nowrap">Move It</span>
                                        </button>
                                        <button
                                            onClick={() => setSuggestionDismissed(true)}
                                            className="flex items-center justify-center rounded-xl bg-white dark:bg-dark-card text-[#0D5F6B] dark:text-dark-teal-light border-2 border-[#0D5F6B] dark:border-dark-teal px-4 py-2 shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-dark"
                                        >
                                            <span className="text-base font-semibold whitespace-nowrap">Keep Schedule</span>
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSuggestionDismissed(true)}
                                    className="w-6 h-6 flex items-center justify-center flex-shrink-0"
                                >
                                    <X className="w-[18px] h-[18px] text-[#7B8C9B]" />
                                </button>
                            </div>
                        </div>
                    </section>
                )
            }

            {/* Projects Section */}
            <section className="mb-4">
                <div className="flex justify-between items-center px-4 mb-3">
                    <span className="text-lg font-semibold text-[#1A2832] dark:text-white">Projects</span>
                    <button
                        onClick={() => setShowProjectModal(true)}
                        className="flex items-center gap-1 text-sm font-semibold text-[#0D5F6B]"
                    >
                        <FolderPlus className="w-4 h-4" />
                        New
                    </button>
                </div>
                <div className="flex gap-3 overflow-x-auto px-4 pb-2 [&::-webkit-scrollbar]:hidden">
                    {/* All Tasks Card */}
                    <button
                        onClick={() => setSelectedProjectId(null)}
                        className={`flex-shrink-0 w-32 p-3 rounded-xl transition ${selectedProjectId === null
                            ? 'bg-[#0D5F6B] dark:bg-dark-teal shadow-[0_4px_16px_rgba(13,95,107,0.15)]'
                            : 'bg-white dark:bg-dark-card shadow-[0_2px_8px_rgba(13,95,107,0.06)] dark:shadow-dark'
                            }`}
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">📋</span>
                            <span className={`text-sm font-semibold ${selectedProjectId === null ? 'text-white' : 'text-[#1A2832] dark:text-dark-text'}`}>
                                All Tasks
                            </span>
                        </div>
                        <div className={`text-xs ${selectedProjectId === null ? 'text-white/80' : 'text-[#7B8C9B] dark:text-dark-text-muted'}`}>
                            {tasks.length} tasks
                        </div>
                    </button>

                    {/* Project Cards */}
                    {projects.map((project) => {
                        const projectTasks = getTasksByProject(project.id);
                        const completed = projectTasks.filter((t) => t.completed).length;
                        const isSelected = selectedProjectId === project.id;

                        return (
                            <div key={project.id} className="relative flex-shrink-0">
                                <button
                                    onClick={() => setSelectedProjectId(isSelected ? null : project.id)}
                                    className={`w-32 p-3 rounded-xl transition ${isSelected
                                        ? 'shadow-[0_4px_16px_rgba(13,95,107,0.15)]'
                                        : 'bg-white dark:bg-dark-card shadow-[0_2px_8px_rgba(13,95,107,0.06)] dark:shadow-dark'
                                        }`}
                                    style={{
                                        backgroundColor: isSelected ? project.color : undefined,
                                    }}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg">{project.icon}</span>
                                            <span
                                                className="text-sm font-semibold truncate max-w-[60px]"
                                                style={{ color: isSelected ? 'white' : project.color }}
                                            >
                                                {project.name}
                                            </span>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenProjectMenuId(openProjectMenuId === project.id ? null : project.id);
                                            }}
                                            className="w-5 h-5 flex items-center justify-center"
                                        >
                                            <MoreHorizontal
                                                className="w-4 h-4"
                                                style={{ color: isSelected ? 'white' : '#7B8C9B' }}
                                            />
                                        </button>
                                    </div>
                                    <div
                                        className="text-xs"
                                        style={{ color: isSelected ? 'white' : '#7B8C9B' }}
                                    >
                                        {completed}/{projectTasks.length} done
                                    </div>
                                    {/* Progress bar */}
                                    <div className="h-1 mt-2 bg-white/30 dark:bg-dark-border rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full"
                                            style={{
                                                width: `${projectTasks.length ? (completed / projectTasks.length) * 100 : 0}%`,
                                                backgroundColor: isSelected ? 'white' : project.color,
                                            }}
                                        />
                                    </div>
                                </button>

                                {/* Project Menu */}
                                {openProjectMenuId === project.id && (
                                    <div className="absolute right-0 top-10 bg-white dark:bg-dark-card rounded-xl shadow-[0_8px_24px_rgba(13,95,107,0.12)] dark:shadow-dark-elevated border border-[#E5EBF0] dark:border-dark-border py-2 z-20 min-w-[120px]">
                                        <button
                                            onClick={() => handleEditProject(project)}
                                            className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-[#1A2832] dark:text-dark-text hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated"
                                        >
                                            <Edit2 className="w-4 h-4 text-[#7B8C9B]" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => {
                                                archiveProject(project.id);
                                                setOpenProjectMenuId(null);
                                            }}
                                            className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-[#1A2832] dark:text-dark-text hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated"
                                        >
                                            <Archive className="w-4 h-4 text-[#7B8C9B]" />
                                            Archive
                                        </button>
                                        <button
                                            onClick={() => handleDeleteProject(project.id)}
                                            className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-[#D66B5C] hover:bg-[#FFEAE7]"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Archived Projects Toggle */}
                {archivedProjects.length > 0 && (
                    <div className="px-4 mt-2">
                        <button
                            onClick={() => setShowArchivedProjects(!showArchivedProjects)}
                            className="flex items-center gap-2 text-sm text-[#7B8C9B] hover:text-[#4A5C6A] transition"
                        >
                            <Archive className="w-4 h-4" />
                            <span>{archivedProjects.length} Archived</span>
                            {showArchivedProjects ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                        </button>
                        {showArchivedProjects && (
                            <div className="flex gap-3 overflow-x-auto mt-2 pb-2 [&::-webkit-scrollbar]:hidden">
                                {archivedProjects.map((project) => (
                                    <div key={project.id} className="relative flex-shrink-0">
                                        <div
                                            className="w-32 p-3 rounded-xl bg-gray-100 dark:bg-dark-elevated opacity-75"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg">{project.icon}</span>
                                                    <span className="text-sm font-semibold text-[#7B8C9B] truncate max-w-[60px]">
                                                        {project.name}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => unarchiveProject(project.id)}
                                                    className="w-5 h-5 flex items-center justify-center text-[#7B8C9B] hover:text-[#0D5F6B]"
                                                    title="Unarchive"
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                            <div className="text-xs text-[#7B8C9B]">
                                                Archived
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Tasks Section */}
            <section className="mb-4 px-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <span className="text-xl font-semibold text-[#1A2832] dark:text-white">
                            {selectedProjectId
                                ? projects.find((p) => p.id === selectedProjectId)?.name || 'Tasks'
                                : 'Today'}
                        </span>
                        <div className="w-6 h-6 rounded-full bg-[#0D5F6B] flex items-center justify-center">
                            <span className="text-xs font-semibold text-white">{displayTasks.length}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Trash Bin Button - Always visible */}
                        <button
                            onClick={() => setShowTrashBin(true)}
                            className="relative w-8 h-8 flex items-center justify-center rounded-lg bg-[#F5F8FA] dark:bg-dark-elevated hover:bg-[#EEF3F6] dark:hover:bg-dark-border transition"
                            title="Trash Bin"
                        >
                            <Trash2 className="w-4 h-4 text-[#7B8C9B]" />
                            {(trashedTasks.length > 0 || trashedEvents.length > 0 || trashedProjects.length > 0) && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                                    <span className="text-[10px] font-bold text-white">
                                        {trashedTasks.length + trashedEvents.length + trashedProjects.length}
                                    </span>
                                </div>
                            )}
                        </button>
                        <div className="flex gap-1 p-1 rounded-lg bg-[#F5F8FA] dark:bg-dark-elevated">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-1 rounded-lg text-sm font-semibold transition ${viewMode === 'list' ? 'bg-[#0D5F6B] dark:bg-dark-teal text-white' : 'text-[#4A5C6A] dark:text-dark-text-secondary'
                                    }`}
                            >
                                List
                            </button>
                            <button
                                onClick={() => setViewMode('time')}
                                className={`px-3 py-1 rounded-lg text-sm font-semibold transition ${viewMode === 'time' ? 'bg-[#0D5F6B] dark:bg-dark-teal text-white' : 'text-[#4A5C6A] dark:text-dark-text-secondary'
                                    }`}
                            >
                                Time
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filter Tags */}
                <div className="flex gap-2 overflow-x-auto mb-4 [&::-webkit-scrollbar]:hidden">
                    {(['all', 'priority'] as const).map((filter) => (
                        <button
                            key={filter}
                            onClick={() => setActiveFilter(filter)}
                            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-dark ${activeFilter === filter
                                ? 'bg-[#0D5F6B] dark:bg-dark-teal text-white border-[#0D5F6B] dark:border-dark-teal'
                                : 'bg-white dark:bg-dark-card text-[#4A5C6A] dark:text-dark-text-secondary border-[#DCE4EA] dark:border-dark-border'
                                }`}
                        >
                            {filter === 'all' ? 'All' : 'Priority'}
                        </button>
                    ))}
                </div>

                {/* Task Cards */}
                <div className="space-y-3">
                    {displayTasks.length === 0 ? (
                        <div className="bg-white dark:bg-dark-card rounded-2xl p-8 text-center shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark">
                            <p className="text-[#7B8C9B] dark:text-dark-text-muted mb-4">
                                {selectedProjectId ? 'No tasks in this project' : 'No tasks yet'}
                            </p>
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="inline-flex items-center gap-2 text-[#0D5F6B] font-semibold"
                            >
                                <Plus className="w-4 h-4" />
                                Add your first task
                            </button>
                        </div>
                    ) : (
                        displayTasks.map((task) => (
                            <TaskCard
                                key={task.id}
                                task={task}
                                onToggle={() => toggleTaskComplete(task.id)}
                                onEdit={() => handleEditTask(task)}
                                onDelete={() => handleDeleteTask(task.id)}
                                onViewDetail={() => navigate(`/app/task/${task.id}`)}
                                isMenuOpen={openMenuId === task.id}
                                onMenuToggle={() => setOpenMenuId(openMenuId === task.id ? null : task.id)}
                                onAddSubtask={(title) => addSubtask(task.id, title)}
                                onToggleSubtask={(subtaskId) => toggleSubtaskComplete(task.id, subtaskId)}
                                onDeleteSubtask={(subtaskId) => deleteSubtask(task.id, subtaskId)}
                            />
                        ))
                    )}
                </div>
            </section>

            {/* Completed Section */}
            {
                completedTasks.length > 0 && (
                    <section className="mb-4 px-4">
                        <button
                            onClick={() => setShowCompleted(!showCompleted)}
                            className="flex items-center gap-2 mb-3 w-full"
                        >
                            <span className="text-base font-semibold text-[#7B8C9B]">Completed</span>
                            <div className="px-2 py-0.5 rounded-full bg-[#EEF3F6]">
                                <span className="text-xs font-semibold text-[#7B8C9B]">
                                    {completedTasks.length}
                                </span>
                            </div>
                            {showCompleted ? (
                                <ChevronUp className="w-4 h-4 text-[#7B8C9B]" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-[#7B8C9B]" />
                            )}
                        </button>

                        {showCompleted && (
                            <div className="space-y-3">
                                {completedTasks.map((task) => (
                                    <TaskCard
                                        key={task.id}
                                        task={task}
                                        onToggle={() => toggleTaskComplete(task.id)}
                                        onEdit={() => handleEditTask(task)}
                                        onDelete={() => handleDeleteTask(task.id)}
                                        onViewDetail={() => navigate(`/app/task/${task.id}`)}
                                        isMenuOpen={openMenuId === task.id}
                                        onMenuToggle={() => setOpenMenuId(openMenuId === task.id ? null : task.id)}
                                        onAddSubtask={(title) => addSubtask(task.id, title)}
                                        onToggleSubtask={(subtaskId) => toggleSubtaskComplete(task.id, subtaskId)}
                                        onDeleteSubtask={(subtaskId) => deleteSubtask(task.id, subtaskId)}
                                    />
                                ))}
                            </div>
                        )}
                    </section>
                )
            }
        </div >
    );
}

// Task Card Component
interface TaskCardProps {
    task: Task;
    onToggle: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onViewDetail: () => void;
    isMenuOpen: boolean;
    onMenuToggle: () => void;
    onAddSubtask: (title: string) => void;
    onToggleSubtask: (subtaskId: string) => void;
    onDeleteSubtask: (subtaskId: string) => void;
}

function TaskCard({
    task,
    onToggle,
    onEdit,
    onDelete,
    onViewDetail,
    isMenuOpen,
    onMenuToggle,
    onAddSubtask,
    onToggleSubtask,
    onDeleteSubtask,
}: TaskCardProps) {
    const [showSubtasks, setShowSubtasks] = useState(false);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [showAddSubtask, setShowAddSubtask] = useState(false);

    const subtasks = task.subtasks || [];
    const completedCount = subtasks.filter((s) => s.completed).length;
    const hasSubtasks = subtasks.length > 0;

    const handleAddSubtask = () => {
        if (newSubtaskTitle.trim()) {
            onAddSubtask(newSubtaskTitle.trim());
            setNewSubtaskTitle('');
            setShowAddSubtask(false);
        }
    };

    return (
        <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] dark:shadow-dark relative">
            <div className="flex items-start gap-3">
                {/* Checkbox */}
                <label className="flex items-center mt-1 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={task.completed}
                        onChange={onToggle}
                        className="sr-only peer"
                    />
                    <div
                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] ${task.completed
                            ? 'bg-[#0D5F6B] dark:bg-dark-teal border-[#0D5F6B] dark:border-dark-teal text-white'
                            : 'bg-white dark:bg-dark-elevated border-[#DCE4EA] dark:border-dark-border text-transparent'
                            }`}
                    >
                        <Check className="w-[14px] h-[14px]" />
                    </div>
                </label>

                {/* Task Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2">
                        <h3
                            onClick={onViewDetail}
                            className={`text-base font-semibold cursor-pointer hover:text-[#0D5F6B] dark:hover:text-dark-teal-light transition-colors ${task.completed ? 'text-[#7B8C9B] dark:text-dark-text-muted line-through' : 'text-[#1A2832] dark:text-dark-text'
                                }`}
                        >
                            {task.title}
                        </h3>
                        <button
                            onClick={onMenuToggle}
                            className="w-6 h-6 flex items-center justify-center flex-shrink-0 hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated rounded"
                        >
                            <MoreHorizontal className="w-[18px] h-[18px] text-[#7B8C9B]" />
                        </button>
                    </div>

                    {/* Description */}
                    {task.description && (
                        <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary mb-2 line-clamp-2">{task.description}</p>
                    )}

                    {/* Date, Time and Priority */}
                    {(task.dueDate || task.timeStart) && (
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                            {task.priority !== 'normal' && (
                                <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: getPriorityColor(task.priority) }}
                                />
                            )}
                            {task.dueDate && (
                                <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3 text-[#7B8C9B]" />
                                    <span className="text-sm text-[#4A5C6A]">
                                        {new Date(task.dueDate + 'T00:00').toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                        })}
                                    </span>
                                </div>
                            )}
                            {task.timeStart && (
                                <span className="text-sm text-[#4A5C6A]">
                                    {task.timeStart} - {task.timeEnd}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Subtasks Progress & Toggle */}
                    {hasSubtasks && (
                        <button
                            onClick={() => setShowSubtasks(!showSubtasks)}
                            className="flex items-center gap-2 mb-3 text-sm text-[#4A5C6A] hover:text-[#1A2832]"
                        >
                            <div className="flex items-center gap-1">
                                <div className="w-16 h-1.5 bg-[#EEF3F6] rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-[#3DA58A] rounded-full transition-all"
                                        style={{ width: `${(completedCount / subtasks.length) * 100}%` }}
                                    />
                                </div>
                                <span className="text-xs font-semibold">
                                    {completedCount}/{subtasks.length}
                                </span>
                            </div>
                            {showSubtasks ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                        </button>
                    )}

                    {/* Subtasks List */}
                    {showSubtasks && (
                        <div className="space-y-2 mb-3 pl-1">
                            {subtasks.map((subtask) => (
                                <div key={subtask.id} className="flex items-center gap-2 group">
                                    <button
                                        onClick={() => onToggleSubtask(subtask.id)}
                                        className={`w-4 h-4 rounded border flex items-center justify-center transition ${subtask.completed
                                            ? 'bg-[#3DA58A] border-[#3DA58A] text-white'
                                            : 'border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated'
                                            }`}
                                    >
                                        {subtask.completed && <Check className="w-3 h-3" />}
                                    </button>
                                    <span
                                        className={`text-sm flex-1 ${subtask.completed ? 'text-[#7B8C9B] dark:text-dark-text-muted line-through' : 'text-[#1A2832] dark:text-dark-text'
                                            }`}
                                    >
                                        {subtask.title}
                                    </span>
                                    <button
                                        onClick={() => onDeleteSubtask(subtask.id)}
                                        className="w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-[#7B8C9B] hover:text-[#D66B5C]"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}

                            {/* Add Subtask */}
                            {showAddSubtask ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={newSubtaskTitle}
                                        onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                        placeholder="Subtask title..."
                                        className="flex-1 text-sm px-2 py-1 border border-[#DCE4EA] rounded focus:outline-none focus:border-[#0D5F6B]"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddSubtask();
                                            if (e.key === 'Escape') {
                                                setShowAddSubtask(false);
                                                setNewSubtaskTitle('');
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={handleAddSubtask}
                                        className="text-xs font-semibold text-[#0D5F6B]"
                                    >
                                        Add
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setShowAddSubtask(true)}
                                    className="flex items-center gap-1 text-xs text-[#0D5F6B] font-semibold"
                                >
                                    <Plus className="w-3 h-3" />
                                    Add subtask
                                </button>
                            )}
                        </div>
                    )}

                    {/* Add Subtask Button (when no subtasks) */}
                    {!hasSubtasks && !showSubtasks && (
                        <button
                            onClick={() => setShowSubtasks(true)}
                            className="flex items-center gap-1 text-xs text-[#7B8C9B] hover:text-[#0D5F6B] mb-3"
                        >
                            <Plus className="w-3 h-3" />
                            Add subtasks
                        </button>
                    )}

                    {/* Show subtask input when clicked on "Add subtasks" for empty task */}
                    {!hasSubtasks && showSubtasks && (
                        <div className="mb-3">
                            {showAddSubtask ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={newSubtaskTitle}
                                        onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                        placeholder="Subtask title..."
                                        className="flex-1 text-sm px-2 py-1 border border-[#DCE4EA] rounded focus:outline-none focus:border-[#0D5F6B]"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddSubtask();
                                            if (e.key === 'Escape') {
                                                setShowAddSubtask(false);
                                                setNewSubtaskTitle('');
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={handleAddSubtask}
                                        className="text-xs font-semibold text-[#0D5F6B]"
                                    >
                                        Add
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setShowAddSubtask(true)}
                                    className="flex items-center gap-1 text-xs text-[#0D5F6B] font-semibold"
                                >
                                    <Plus className="w-3 h-3" />
                                    Add subtask
                                </button>
                            )}
                        </div>
                    )}

                    {/* Context Badge */}
                    <div className="flex items-center gap-2">
                        <div
                            className="px-3 py-1 rounded-lg"
                            style={{
                                backgroundColor: task.contextBgColor,
                                color: task.contextColor,
                            }}
                        >
                            <span className="text-sm font-semibold">{task.context}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dropdown Menu */}
            {isMenuOpen && (
                <div className="absolute right-4 top-12 bg-white dark:bg-dark-card rounded-xl shadow-[0_8px_24px_rgba(13,95,107,0.12)] dark:shadow-dark-elevated border border-[#E5EBF0] dark:border-dark-border py-2 z-10 min-w-[140px]">
                    <button
                        onClick={onEdit}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-[#1A2832] dark:text-dark-text hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated"
                    >
                        <Edit2 className="w-4 h-4 text-[#7B8C9B]" />
                        Edit
                    </button>
                    <button
                        onClick={onDelete}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-[#D66B5C] hover:bg-[#FFEAE7]"
                    >
                        <Trash2 className="w-4 h-4" />
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
}

// Trash Bin Modal Component
interface TrashBinModalProps {
    trashedTasks: Task[];
    trashedEvents: CalendarEvent[];
    trashedProjects: Project[];
    onRestoreTask: (id: string) => void;
    onRestoreEvent: (id: string) => void;
    onRestoreProject: (id: string) => void;
    onDeleteTask: (id: string) => void;
    onDeleteEvent: (id: string) => void;
    onDeleteProject: (id: string) => void;
    onClose: () => void;
}

function TrashBinModal({
    trashedTasks,
    trashedEvents,
    trashedProjects,
    onRestoreTask,
    onRestoreEvent,
    onRestoreProject,
    onDeleteTask,
    onDeleteEvent,
    onDeleteProject,
    onClose,
}: TrashBinModalProps) {
    const [activeTab, setActiveTab] = useState<'tasks' | 'events' | 'projects'>('tasks');

    const formatDeletedDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    const handleRestoreAll = () => {
        if (activeTab === 'tasks') {
            trashedTasks.forEach((task) => onRestoreTask(task.id));
        } else if (activeTab === 'events') {
            trashedEvents.forEach((event) => onRestoreEvent(event.id));
        } else {
            trashedProjects.forEach((project) => onRestoreProject(project.id));
        }
    };

    const handleDeleteAll = () => {
        if (activeTab === 'tasks') {
            trashedTasks.forEach((task) => onDeleteTask(task.id));
        } else if (activeTab === 'events') {
            trashedEvents.forEach((event) => onDeleteEvent(event.id));
        } else {
            trashedProjects.forEach((project) => onDeleteProject(project.id));
        }
    };

    const currentItems = activeTab === 'tasks'
        ? trashedTasks
        : activeTab === 'events'
            ? trashedEvents
            : trashedProjects;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white dark:bg-dark-card w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-[#EEF3F6] dark:border-dark-border flex-shrink-0">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-[#1A2832] dark:text-dark-text flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-[#7B8C9B]" />
                            Trash Bin
                        </h2>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated"
                        >
                            <X className="w-5 h-5 text-[#7B8C9B]" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setActiveTab('tasks')}
                            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${activeTab === 'tasks'
                                ? 'bg-[#0D5F6B] text-white'
                                : 'bg-[#F5F8FA] text-[#4A5C6A]'
                                }`}
                        >
                            Tasks ({trashedTasks.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('events')}
                            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${activeTab === 'events'
                                ? 'bg-[#0D5F6B] text-white'
                                : 'bg-[#F5F8FA] text-[#4A5C6A]'
                                }`}
                        >
                            Events ({trashedEvents.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('projects')}
                            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${activeTab === 'projects'
                                ? 'bg-[#0D5F6B] text-white'
                                : 'bg-[#F5F8FA] text-[#4A5C6A]'
                                }`}
                        >
                            Projects ({trashedProjects.length})
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {currentItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-[#F5F8FA] flex items-center justify-center mb-4">
                                <Trash2 className="w-8 h-8 text-[#DCE4EA]" />
                            </div>
                            <p className="text-[#7B8C9B] text-sm">
                                No deleted {activeTab}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {activeTab === 'tasks'
                                ? trashedTasks.map((task) => (
                                    <div
                                        key={task.id}
                                        className="bg-[#F5F8FA] rounded-xl p-3 flex items-start justify-between gap-3"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-[#1A2832] truncate">
                                                {task.title}
                                            </p>
                                            <p className="text-xs text-[#7B8C9B]">
                                                Deleted {task.deletedAt && formatDeletedDate(task.deletedAt)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <button
                                                onClick={() => onRestoreTask(task.id)}
                                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#D9EEF2] transition"
                                                title="Restore"
                                            >
                                                <RotateCcw className="w-4 h-4 text-[#0D5F6B]" />
                                            </button>
                                            <button
                                                onClick={() => onDeleteTask(task.id)}
                                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 transition"
                                                title="Delete Forever"
                                            >
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                                : activeTab === 'events'
                                    ? trashedEvents.map((event) => (
                                        <div
                                            key={event.id}
                                            className="bg-[#F5F8FA] rounded-xl p-3 flex items-start justify-between gap-3"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-[#1A2832] truncate">
                                                    {event.title}
                                                </p>
                                                <p className="text-xs text-[#7B8C9B]">
                                                    {event.timeStart} - {event.timeEnd}
                                                    {event.deletedAt && ` • Deleted ${formatDeletedDate(event.deletedAt)}`}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <button
                                                    onClick={() => onRestoreEvent(event.id)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#D9EEF2] transition"
                                                    title="Restore"
                                                >
                                                    <RotateCcw className="w-4 h-4 text-[#0D5F6B]" />
                                                </button>
                                                <button
                                                    onClick={() => onDeleteEvent(event.id)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 transition"
                                                    title="Delete Forever"
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                    : trashedProjects.map((project) => (
                                        <div
                                            key={project.id}
                                            className="bg-[#F5F8FA] rounded-xl p-3 flex items-start justify-between gap-3"
                                        >
                                            <div className="flex-1 min-w-0 flex items-center gap-2">
                                                <span className="text-lg">{project.icon || '📁'}</span>
                                                <div>
                                                    <p className="text-sm font-medium text-[#1A2832] truncate">
                                                        {project.name}
                                                    </p>
                                                    <p className="text-xs text-[#7B8C9B]">
                                                        Deleted {project.deletedAt && formatDeletedDate(project.deletedAt)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <button
                                                    onClick={() => onRestoreProject(project.id)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#D9EEF2] transition"
                                                    title="Restore"
                                                >
                                                    <RotateCcw className="w-4 h-4 text-[#0D5F6B]" />
                                                </button>
                                                <button
                                                    onClick={() => onDeleteProject(project.id)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 transition"
                                                    title="Delete Forever"
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                {currentItems.length > 0 && (
                    <div className="p-4 border-t border-[#EEF3F6] flex gap-3 flex-shrink-0">
                        <button
                            onClick={handleRestoreAll}
                            className="flex-1 py-3 rounded-xl bg-[#D9EEF2] text-[#0D5F6B] font-semibold flex items-center justify-center gap-2"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Restore All
                        </button>
                        <button
                            onClick={handleDeleteAll}
                            className="flex-1 py-3 rounded-xl bg-red-100 text-red-600 font-semibold flex items-center justify-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete All Forever
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
